<template>
  <section class="position-assistant mb-3" dir="rtl" aria-label="מילוי מהיר עם AI">
    <div class="position-assistant-bar">
      <AiBot :working="busy" />
      <button type="button" class="button is-info is-small" :disabled="busy || disabled || !rawText.trim()" @click="send">מילוי מהיר עם AI מתוך Company Raw Text</button>
    </div>
    <p v-if="busy" class="is-size-7 mt-1">הבוט ממלא את האזור המסומן בוורוד; השדות שהוא ממלא בו נעולים עד שיסיים. שאר הטופס פתוח לעריכה.</p>
    <p v-if="error" role="alert">{{ error }} <button type="button" class="button is-small" :disabled="busy || disabled" @click="send">ניסיון נוסף</button></p>
    <details v-if="session.used" class="mt-2">
      <summary>דירוג העוזר ★</summary>
      <div v-for="metric in ratingMetrics" :key="metric.key" role="group" :aria-label="metric.label" class="mt-2">
        <span>{{ metric.label }}</span>
        <button v-for="star in 5" :key="star" type="button" class="button is-small" :class="{ 'is-warning': session.ratings[metric.key] >= star }" :aria-label="metric.label + ': ' + star + ' מתוך 5'" :aria-pressed="session.ratings[metric.key] === star" :disabled="ratingBusy || session.ratingSent || disabled" @click="session.ratings[metric.key] = star">★</button>
      </div>
      <label :for="uid + '-feedback'">משוב נוסף</label>
      <textarea :id="uid + '-feedback'" v-model="session.feedback" class="textarea mt-2" rows="2" maxlength="2000" :disabled="ratingBusy || session.ratingSent || disabled" />
      <button type="button" class="button is-small mt-2" :disabled="ratingBusy || session.ratingSent || disabled || !session.ratings.performance || !session.ratings.usefulness" @click="rate">שליחת דירוג</button>
      <p v-if="ratingError" role="alert">{{ ratingError }}</p>
      <p v-if="session.ratingSent" role="status">תודה! הדירוג נשמר.</p>
    </details>
  </section>
</template>
<script>
import AiBot from '@/components/AiBot.vue'
import { copy, same, POSITION_ASSISTANT_AREAS, assistantSaveUnits } from '@/custom/classes/position_assistant'
export default {
  components: { AiBot },
  props: {
    session: { type: Object, required: true },
    draft: { type: Object, required: true },
    rawText: { type: String, default: '' },
    companyId: { type: [Number, String], required: true },
    apply: { type: Function, required: true },
    disabled: Boolean
  },
  data () {
    return { applyingKeys: [],
      pendingFields: [],
      workingArea: null,
      completedUnits: [],
      completedRaw: null,
      revisions: {},
      observedDraft: copy(this.draft),
      uid: 'position-assistant-' + this._uid,
      busy: false,
      error: '',
      ratingBusy: false,
      ratingError: '',
      ratingMetrics: [{ key: 'performance', label: 'דיוק וביצועים' }, { key: 'usefulness', label: 'מידת העזרה' }] }
  },
  computed: {
    workingFields () { return this.pendingFields.filter(key => !this.revisions[key]) }
  },
  watch: { busy (value) { this.$emit('busy', value) },
    workingArea (area) { this.$emit('working-area', area) },
    workingFields (fields) { this.$emit('working-fields', fields) },
    draft: { deep: true,
      handler (draft) {
        for (const key of Object.keys(draft)) {
          if (!this.applyingKeys.includes(key) && !same(draft[key], this.observedDraft[key])) this.$set(this.revisions, key, (this.revisions[key] || 0) + 1)
        }
        this.observedDraft = copy(draft)
      } } },
  beforeDestroy () { this.$emit('working-fields', []); this.$emit('working-area', null) },
  methods: {
    async send () {
      if (this.busy || this.disabled || !this.rawText.trim()) return
      this.busy = true
      this.error = ''
      this.session.used = true
      const raw = this.rawText
      if (this.completedRaw !== raw) { this.completedUnits = []; this.completedRaw = raw }
      const open = unit => !this.completedUnits.includes(unit[0]) && unit.every(key => key in this.draft && !this.revisions[key])
      try {
        for (const area of POSITION_ASSISTANT_AREAS) {
          if (this._isDestroyed) return
          if (raw !== this.rawText) throw new Error('Raw text changed')
          const units = assistantSaveUnits(area.keys).filter(open)
          if (!units.length) continue
          const keys = units.flat()
          const draft = copy(this.draft)
          this.workingArea = area.id
          this.pendingFields = keys
          const result = await this.$network_utils.positionCreationAssistant({ current_company_id: this.companyId,
            stage: 'details',
            field_keys: keys,
            raw_company_text: raw,
            draft })
          if (this._isDestroyed) return
          if (raw !== this.rawText) throw new Error('Raw text changed')
          if (Object.keys(result.suggestions).some(key => !keys.includes(key))) throw new Error('Unexpected field')
          for (const unit of units) {
            if (unit.every(key => !this.revisions[key] && same(draft[key], this.draft[key]))) {
              const values = {}
              for (const key of unit) {
                const value = result.suggestions[key]
                if (value != null && value !== '' && !same(value, this.draft[key])) values[key] = copy(value)
              }
              if (Object.keys(values).length) {
                this.applyingKeys = unit
                try {
                  await this.apply(unit.length === 2 ? 'seniority_range' : unit[0], unit.length === 2 ? values : values[unit[0]], draft)
                } finally { await this.$nextTick(); this.applyingKeys = [] }
              }
            }
            this.completedUnits.push(unit[0])
            this.pendingFields = this.pendingFields.filter(key => !unit.includes(key))
          }
          this.workingArea = null
        }
      } catch (error) {
        this.error = raw !== this.rawText ? 'טקסט החברה השתנה. יש לנסות שוב.' : 'המילוי נעצר. הפרטים שכבר מולאו נשארו בטופס; ניסיון נוסף ימשיך מהשדה שלא הושלם.'
      } finally { this.pendingFields = []; this.workingArea = null; this.busy = false }
    },
    async rate () {
      if (this.ratingBusy || this.session.ratingSent || !this.session.ratings.performance || !this.session.ratings.usefulness) return
      this.ratingBusy = true
      this.ratingError = ''
      try {
        await this.$network_utils.ratePositionAssistant({ current_company_id: this.companyId, ...this.session.ratings, feedback: this.session.feedback })
        this.session.ratingSent = true
      } catch (error) { this.ratingError = 'שמירת הדירוג נכשלה. אפשר לנסות שוב.' } finally { this.ratingBusy = false }
    }
  }
}
</script>

<style scoped>
.position-assistant-bar { display: flex; align-items: center; gap: 10px; }
</style>

<style>
/* Field box the bot is working on (set by positionAssistantMixin). Global:
   the box is the root of another component. */
.ai-field-working {
  outline: 2px solid #ed1764;
  outline-offset: 3px;
  border-radius: 6px;
  background-color: #fff5f9;
  box-shadow: 0 0 0 6px rgba(237, 23, 100, 0.12);
  transition: outline-color 0.2s, box-shadow 0.2s;
}
</style>
